import { useState, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Stack,
  FormControlLabel,
  Checkbox,
  Chip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getFunctions, httpsCallable } from "firebase/functions";
import { nanoid } from "nanoid";
import logEvent from "../helpers/logEvent";
import AccountContext from "../context/AccountContext";
import signIn from "../helpers/signIn";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useRouter } from "next/router";

const dayOptions = [...Array(8).keys()];
const hourOptions = [...Array(24).keys()];
const minuteOptions = [...Array(60).keys()];

export default function Home(props) {
  const router = useRouter();
  const { firebaseUser, accountId, subscription, loadedSubscription, subscriptionDoc } = useContext(AccountContext);
  const [question, setQuestion] = useState("");
  const [name, setName] = useState("");
  const [day, setDay] = useState(0);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [choices, setChoices] = useState(["", ""]);
  const [showLoader, setShowLoader] = useState(false);
  const [signed, setSigned] = useState(false);
  const [hidePublicVoteCount, setHidePublicVoteCount] = useState(false);
  const [disableAutoSubmit, setDisableAutoSubmit] = useState(false);
  const [privatePoll, setPrivatePoll] = useState(false);
  const [signedAttempt, setSignedAttempt] = useState(0);
  const [shake, setShake] = useState("");
  // const [pid, setPid] = useState("");

  const handleChoicesChange = (value, idx) => {
    if (value.length > 28) return;
    setChoices(
      choices.map((item, index) => {
        return idx === index ? value : item;
      })
    );
  };

  const handleSubmit = async () => {
    setShowLoader(true);
    const pid = nanoid(6);
    try {
      await httpsCallable(
        getFunctions(),
        "addPoll"
      )({
        pid,
        name,
        choices,
        question,
        accountId: firebaseUser && accountId ? accountId : null,
        signed,
        hidePublicVoteCount,
        disableAutoSubmit,
        privatePoll,
        endAtObj: { day, hour, minute },
      });
      logEvent("addedPoll");
      router.push("/[pid]/share", `/${pid}/share`);
    } catch (error) {
      props.showSnack("Something went wrong. Please try again.");
      setShowLoader(false);
    }
  };

  const choicesMarkUp = choices.map((row, idx) => {
    return (
      <Box key={idx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            label={`Choice ${idx + 1}`}
            variant="outlined"
            disabled={showLoader}
            value={row}
            size="small"
            onChange={event => handleChoicesChange(event.target.value, idx)}
          />
          {idx == choices.length - 1 && idx > 1 && (
            <IconButton
              size="small"
              disabled={showLoader}
              onClick={e => {
                e.preventDefault();
                setChoices(prev => prev.slice(0, -1));
              }}
            >
              <DeleteRoundedIcon />
            </IconButton>
          )}
        </Stack>
      </Box>
    );
  });

  const selectMarkup = (options, label, value, setValue) => {
    return (
      <FormControl variant="standard" sx={{ mt: 1, mb: 1, minWidth: 48 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value}
          disabled={showLoader}
          variant="standard"
          onChange={event => {
            setValue(event.target.value);
          }}
        >
          {options.map((row, idx) => {
            return (
              <MenuItem key={idx} value={row}>
                {row}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  const mainMarkup = (
    <Card>
      <CardContent>
        <Typography variant="h5" component="h1">
          Create a new poll
        </Typography>
        <TextField
          label="Name (optional)"
          size="small"
          disabled={showLoader}
          value={name}
          variant="standard"
          onChange={event => {
            event.target.value.length < 25 && setName(event.target.value);
          }}
        />
        <TextField
          label="Ask a question..."
          size="small"
          fullWidth
          disabled={showLoader}
          value={question}
          variant="standard"
          onChange={event => {
            event.target.value.length < 140 && setQuestion(event.target.value);
          }}
        />
        <Stack spacing={1} sx={{ mt: 2, mb: 1 }}>
          {choicesMarkUp}
        </Stack>
        {choices.length < 4 && (
          <IconButton
            size="small"
            disabled={showLoader}
            onClick={e => {
              e.preventDefault();
              setChoices(prev => [...prev, ""]);
            }}
          >
            <AddCircleOutlineOutlinedIcon />
          </IconButton>
        )}
        <Typography variant="subtitle1" component="h3" sx={{ mt: 1 }}>
          Poll duration
        </Typography>
        <Stack direction="row" spacing={4} alignItems="center" sx={{ mb: 1 }}>
          {selectMarkup(dayOptions, "Days", day, setDay)}
          {selectMarkup(hourOptions, "Hours", hour, setHour)}
          {selectMarkup(minuteOptions, "Minutes", minute, setMinute)}
        </Stack>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                sx={{ pt: 0, pb: 0, pr: "2px" }}
                checked={signed}
                onChange={event => {
                  signedAttempt < 1 && logEvent("clickedCollectData");
                  setSignedAttempt(signedAttempt + 1);
                  if (subscription?.isSubscribed) {
                    setSigned(event.target.checked);
                  } else {
                    setShake("shakeit");
                    setTimeout(() => {
                      setShake("");
                    }, 1000);
                  }
                }}
              />
            }
            label={
              <Typography sx={{ mt: { xs: "2px", lg: 0 } }} variant="body2">
                Collect voter names &amp; emails
              </Typography>
            }
          />
        </Box>
        {!firebaseUser && signedAttempt > 0 && (
          <Box sx={{ mb: 2 }} className={shake}>
            <Chip
              label="Sign In for this feature"
              color="secondary"
              size="small"
              onClick={e => {
                e.preventDefault();
                signIn(props.auth);
                logEvent("clickedSignInForFeature");
              }}
            />
          </Box>
        )}
        {firebaseUser && loadedSubscription && !subscription?.isSubscribed && signedAttempt > 0 && (
          <Box sx={{ mb: 2 }} className={shake}>
            <Chip
              label={subscriptionDoc?.first_trial_start ? "Upgrade for this feature" : "Upgrade for this feature"}
              color="secondary"
              size="small"
              icon={<WorkspacePremiumIcon />}
              onClick={e => {
                e.preventDefault();
                logEvent("clickedUpgradeForFeature");
                router.push("/billing");
              }}
            />
          </Box>
        )}
        {firebaseUser && subscription?.isSubscribed && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  sx={{ pt: 0, pb: 0, pr: "2px" }}
                  checked={hidePublicVoteCount}
                  onChange={event => {
                    setHidePublicVoteCount(event.target.checked);
                  }}
                />
              }
              label={
                <Typography sx={{ mt: { xs: "2px", lg: 0 } }} variant="body2">
                  Hide results from voters
                </Typography>
              }
            />
          </Box>
        )}
        <LoadingButton
          sx={{ mt: 2 }}
          onClick={e => {
            e.preventDefault();
            handleSubmit();
          }}
          variant="contained"
          color="primary"
          loading={showLoader}
          disabled={
            !(question && choices.filter(row => row == "").length == 0 && day + hour + minute > 0) || showLoader
          }
        >
          Submit
        </LoadingButton>
        <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
          <InfoOutlinedIcon fontSize="small" color="disabled" />
          <Typography variant="caption" component="h6" color="textSecondary">
            &nbsp;Polls are public
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  return mainMarkup;
}
